import type { GridApi, GridRowId } from '@mui/x-data-grid-pro';
import type { QueryClient } from '@tanstack/react-query';
import { notifyManager, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ItemsApi } from '@/common/api/items';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/common/api/types/items';
import type { TreeSetPredicate } from '@/common/queries/itemsTreeSetConsistency';
import {
  cancelPredicateReconciliation,
  invalidateItemsTreeSet,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { isPriorityOrderedTreeSetKey } from '@/features/items/queryKeys/itemsTreeSetKey';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';
import {
  computeGroupBlockMove,
  type GroupBlockMove,
  type RowPlacement,
  toRowPlacements,
} from '../row-reordering/groupBlockMove';
import { claimRows } from './rowReorderClaims';
import {
  evaluateReordered,
  forgetRollbackBaselines,
  reassertCycleGuardFor,
  reorderTreeSetData,
} from './useItemRelativePriorityUpdate';
import type { RowMoveResult } from './useItemReparent';

export interface GroupReorderRequest {
  move: GroupBlockMove;
  previousOrder: readonly GridRowId[];
}

const placeEntries = (
  entries: readonly ItemTreeSetEntry[],
  placements: readonly RowPlacement[],
): readonly ItemTreeSetEntry[] =>
  placements.reduce<readonly ItemTreeSetEntry[]>(
    (placed, { id, targetId, position }) =>
      reorderTreeSetData(placed, Number(id), Number(targetId), position),
    entries,
  );

const sameOrder = (a: readonly ItemTreeSetEntry[], b: readonly ItemTreeSetEntry[]): boolean =>
  a.length === b.length && a.every((entry, i) => entry.id === b[i]?.id);

const placeInCache = (queryClient: QueryClient, placements: readonly RowPlacement[]) => {
  queryClient.setQueriesData<ItemTreeSet>(
    { queryKey: commonQueryKeys.itemsTreeSet.all(), predicate: isPriorityOrderedTreeSetKey },
    (cached) => {
      if (!cached) return cached;
      const data = placeEntries(cached.data, placements);
      if (data === cached.data) return cached;
      return { ...cached, data: [...data] };
    },
  );
};

const reassertPlacements = (queryClient: QueryClient, placements: readonly RowPlacement[]) => {
  const guard = reassertCycleGuardFor(queryClient);
  const writeBlock = placements.reduceRight<() => void>(
    (inner, { id, targetId, position }) =>
      () => {
        guard.attempt(Number(id), Number(targetId), position, inner);
      },
    () => placeInCache(queryClient, placements),
  );
  writeBlock();
};

const treeSetBlockReordered =
  (movedIds: readonly number[], anchor: GroupBlockMove['anchor']): TreeSetPredicate =>
  (entries) => {
    const anchorId = Number(anchor.id);
    const anchorEntry = entries.find((entry) => entry.id === anchorId);
    const presentIds = new Set(entries.map((entry) => entry.id));
    const expectedBlock = movedIds.filter((id) => presentIds.has(id));
    if (expectedBlock.length === 0) return true;
    if (!anchorEntry) return false;

    const parentId = anchorEntry.parentId ?? null;
    const siblingIds = entries
      .filter((entry) => (entry.parentId ?? null) === parentId)
      .map((entry) => entry.id);
    const anchorIdx = siblingIds.indexOf(anchorId);
    const actualBlock =
      anchor.type === 'after'
        ? siblingIds.slice(anchorIdx + 1, anchorIdx + 1 + expectedBlock.length)
        : siblingIds.slice(Math.max(0, anchorIdx - expectedBlock.length), anchorIdx);
    return (
      actualBlock.length === expectedBlock.length &&
      actualBlock.every((id, i) => id === expectedBlock[i])
    );
  };

// Failed rows kept their old priority server-side; moved ones sit as a block at the anchor.
const orderAfterPartialFailure = (
  previousOrder: readonly GridRowId[],
  movedIds: readonly number[],
  anchor: GroupBlockMove['anchor'],
): readonly GridRowId[] =>
  computeGroupBlockMove(previousOrder, movedIds, {
    rowId: anchor.id,
    position: anchor.type === 'after' ? 'below' : 'above',
  })?.finalOrder ?? previousOrder;

let nextGroupReorder = 0;

export const useItemGroupReorder = (apiRef: React.RefObject<GridApi>, instanceKey: string) => {
  const queryClient = useQueryClient();

  return React.useCallback(
    async ({ move, previousOrder }: GroupReorderRequest): Promise<RowMoveResult> => {
      const { orderedIds, finalOrder, anchor } = move;
      const itemIds = orderedIds.map(Number);

      const claim = claimRows(queryClient, itemIds);
      forgetRollbackBaselines(queryClient, itemIds);
      const guard = reassertCycleGuardFor(queryClient);
      for (const id of itemIds) guard.clear(id);
      const supersedeKey = `reorder-group:${++nextGroupReorder}`;
      claim.onSupersede(() => cancelPredicateReconciliation(queryClient, supersedeKey));

      let target = {
        placements: toRowPlacements(finalOrder, orderedIds),
        isReflected: treeSetBlockReordered(itemIds, anchor),
      };

      // Deferred past the drop frame so the cache rebuild doesn't race setRowPosition's repaint.
      const cacheHandle = requestAnimationFrame(() => placeInCache(queryClient, target.placements));
      claim.onSupersede(() => cancelAnimationFrame(cacheHandle));

      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        if (!claim.isCurrent()) return;
        if (event.type !== 'updated' || event.action.type !== 'success') return;
        if (!isPriorityOrderedTreeSetKey({ queryKey: event.query.queryKey })) return;
        const data = event.query.state.data as ItemTreeSet | undefined;
        if (!data || target.isReflected(data.data)) return;

        if (sameOrder(placeEntries(data.data, target.placements), data.data)) return;
        reassertPlacements(queryClient, target.placements);
      });
      claim.onSupersede(unsubscribe);

      let failedIds: Set<number>;
      try {
        const response = await ItemsApi.bulkPartialPatchItems({
          itemIds,
          fields: { relativePriority: { id: Number(anchor.id), type: anchor.type } },
        });
        failedIds = new Set(response.failed.map((failure) => failure.itemId));
      } catch {
        failedIds = new Set(itemIds);
      }
      const confirmedAt = performance.now();
      const movedIds = itemIds.filter((id) => !failedIds.has(id));
      const outcome = { failedCount: failedIds.size, superseded: !claim.isCurrent() };

      if (outcome.superseded) return outcome;

      if (failedIds.size > 0) {
        cancelAnimationFrame(cacheHandle);
        target = {
          placements: toRowPlacements(
            orderAfterPartialFailure(previousOrder, movedIds, anchor),
            orderedIds,
          ),
          isReflected: treeSetBlockReordered(movedIds, anchor),
        };
        const api = apiRef.current;
        for (const { id, targetId, position } of target.placements) {
          api?.setRowPosition(id, targetId, position);
        }
        placeInCache(queryClient, target.placements);
        bumpVisibleItemsCache(queryClient, instanceKey);
      }

      const [firstMovedId] = movedIds;
      if (firstMovedId === undefined) {
        unsubscribe();
        claim.release();
        return outcome;
      }

      void invalidateItemsTreeSet(queryClient);
      void reconcileAndSettle(queryClient, movedIds, {
        isReflected: target.isReflected,
        evaluateReflected: evaluateReordered(target.isReflected, firstMovedId, confirmedAt),
        supersedeKey,
      })
        .catch(() => {})
        .finally(() => {
          notifyManager.schedule(() => {
            unsubscribe();
            claim.release();
          });
        });

      return outcome;
    },
    [apiRef, queryClient, instanceKey],
  );
};
